import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Wand2, Library, Copy, Mic, Settings, LogOut, Sun, Moon, LayoutDashboard, FileAudio, PenTool, Zap, Gift, Crown, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Overview from '../components/dashboard/Overview';
import TTSStudio from '../components/dashboard/TTSStudio';
import VoiceLibrary from '../components/dashboard/VoiceLibrary';
import VoiceCloning from '../components/dashboard/VoiceCloning';
import SpeechToText from '../components/dashboard/SpeechToText';
import SettingsPage from '../components/dashboard/SettingsPage';
import ReferralPage from '../components/dashboard/ReferralPage';
import ScrollToTop from '../components/ScrollToTop';
import axios from 'axios';

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'tts', label: 'Text to Speech', icon: Wand2 },
  { id: 'voices', label: 'Voice Library', icon: Library },
  { id: 'cloning', label: 'Voice Cloning', icon: Copy },
  { id: 'stt', label: 'Speech to Text', icon: FileAudio },
  { id: 'referral', label: 'Referral', icon: Gift },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function Dashboard() {
  const { user, logout, token, API } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'overview');
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.some(x => x.id === t)) setTab(t);
  }, [searchParams]);

  useEffect(() => {
    const titleMap = {
      overview: 'Dashboard', tts: 'Text to Speech', voices: 'Voice Library',
      cloning: 'Voice Cloning', stt: 'Speech to Text', referral: 'Referral', settings: 'Settings'
    };
    document.title = `${titleMap[tab] || 'Dashboard'} | VoiceraLab`;
  }, [tab]);

  const changeTab = (t) => { setTab(t); setSearchParams({ tab: t }); };
  const handleLogout = () => { logout(); navigate('/'); };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await axios.post(`${API}/payments/checkout?plan_id=creator`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch { /* ignore */ }
    setUpgrading(false);
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card flex flex-col flex-shrink-0 hidden lg:flex">
        {/* Logo */}
        <div className="p-5 border-b border-border flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Mic className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-lg font-outfit text-foreground">VoiceraLab</span>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`nav-${t.id}`}
                onClick={() => changeTab(t.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>

        {/* Bottom: Upgrade + Logout */}
        <div className="p-3 border-t border-border space-y-2">
          {(!user?.plan || user?.plan === 'free') && (
            <button
              data-testid="sidebar-upgrade-btn"
              onClick={handleUpgrade}
              disabled={upgrading}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {upgrading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Crown className="w-4 h-4" />}
              Upgrade Now
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className="h-14 border-b border-border flex items-center justify-between px-5 flex-shrink-0 bg-card">
          {/* Mobile menu */}
          <div className="flex items-center gap-3 lg:hidden">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Mic className="w-3.5 h-3.5 text-white" />
            </div>
            <select value={tab} onChange={e => changeTab(e.target.value)} className="bg-background border border-border rounded-lg px-2 py-1 text-sm text-foreground">
              {TABS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          <div className="hidden lg:flex items-center gap-2">
            <h1 className="text-sm font-medium text-foreground">{TABS.find(t => t.id === tab)?.label}</h1>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-xs">
              <Zap className="w-3 h-3 text-primary" />
              <span className="text-foreground font-medium">{Math.max(0, (user?.credits || 10000) - (user?.credits_used || 0)).toLocaleString()}</span>
              <span className="text-muted-foreground">credits</span>
            </div>
            {(!user?.plan || user?.plan === 'free') && (
              <button
                data-testid="topbar-upgrade-btn"
                onClick={handleUpgrade}
                disabled={upgrading}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs font-medium hover:opacity-90 transition-opacity"
              >
                <CreditCard className="w-3 h-3" /> Upgrade
              </button>
            )}
            <button onClick={toggleTheme} className="p-2 rounded-lg bg-secondary hover:bg-secondary/70 transition-colors">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
              {user?.name?.[0] || 'U'}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-5 sm:p-6">
          {tab === 'overview' && <Overview />}
          {tab === 'tts' && <TTSStudio />}
          {tab === 'voices' && <VoiceLibrary />}
          {tab === 'cloning' && <VoiceCloning />}
          {tab === 'stt' && <SpeechToText />}
          {tab === 'referral' && <ReferralPage />}
          {tab === 'settings' && <SettingsPage />}
        </main>
      </div>
      <ScrollToTop />
    </div>
  );
}
